/* ═══════════════════════════════════════════════════════════════════════════
   L'HORLOGE DU HÉRAUT — source unique de vérité du cycle de combat.

   Ce fichier n'est PAS un `.def.ts` : le registre ne le ramasse pas, il n'est
   qu'un module de constantes partagé par le noyau et ses parties.

   Pourquoi l'extraire au lieu d'écrire les nombres dans chaque fichier : le
   moteur n'a aucune notion de « phase ». Chaque partie possède son propre
   émetteur, et sa cadence est un simple couple (firstDelay, every) décompté
   depuis sa naissance. La synchronisation des six armes du boss n'existe donc
   que parce que TOUS les firstDelay sont calculés à partir de la même origine
   et que TOUS les `every` valent exactement CYCLE.

   Le jour où quelqu'un règle « juste un peu » le délai d'un bras dans son
   propre fichier, le boss ne se désynchronise pas visiblement : il perd
   simplement sa fenêtre de punition, et le combat devient un mur d'attrition
   que personne ne saura expliquer. D'où la constante partagée et l'assertion
   plus bas — elles rendent cette panne impossible plutôt qu'improbable.

   ⚠️ Toutes les parties héritent du télégraphe du porteur et naissent au même
   tick que lui (levelSystem.spawnParts). Leur `age` est donc rigoureusement
   celui du noyau : c'est ce qui autorise une horloge commune sans le moindre
   état partagé côté simulation.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Durée d'un cycle complet, en secondes. C'est aussi la valeur OBLIGATOIRE de
 *  `every` pour chaque émetteur du boss : une arme dont la période ne divise
 *  pas le cycle finirait par tirer dans la fenêtre de punition. */
export const CYCLE = 7.2;

/** Âge auquel le combat commence réellement.
 *
 *  Il couvre le télégraphe (2,2 s) ET la descente jusqu'à la station : le boss
 *  apparaît vers Y = +700 et se pose à Y = +330, ce qui prend ~2,6 s à 170 u/s
 *  avec l'accélération d'entrée. Démarrer le cycle plus tôt ferait tirer les
 *  canons pendant que le vaisseau descend encore — l'entrée cesserait d'être
 *  une entrée pour devenir une agression, et le joueur n'aurait jamais le temps
 *  de LIRE la silhouette qu'il doit démonter. */
export const COMBAT_START = 3.6;

/* ── Découpage du cycle, en secondes depuis son début ────────────────────────

     0,0 ─── 2,8   POURSUITE
                   Le porteur glisse vers l'abscisse du joueur, lentement
                   (190 u/s contre 480 au joueur). C'est LA leçon du combat :
                   le joueur pilote le boss. Il peut le traîner dans un coin.
       0,4         canons   — deux éventails statiques, la géographie
       1,4         nacelles — deux anneaux lents, le volume
       2,2         noyau    — un grand éventail lent, le plafond

     2,8 ─── 3,6   VERROU
                   Le porteur s'immobilise NET. 0,8 s pendant lesquelles rien
                   ne part : c'est le télégraphe de la salve, et il ne coûte
                   aucun système dédié — l'arrêt du mouvement EST le signal.

     3,6           SALVE — les deux bras tirent en visée croisée.

     3,6 ─── 5,2   La salve traverse (460 u/s sur ~700 u).

     5,2 ─── 7,2   PUNITION — 2,0 s. Aucune émission, le porteur immobile.
                   C'est la fenêtre exigée par PLAN.md §6.6 (≥ 1,8 s), et c'est
                   elle qui fait qu'un boss se COMBAT au lieu de se subir.
   ────────────────────────────────────────────────────────────────────────── */

export const PHASE_CANONS = 0.4;
export const PHASE_NACELLES = 1.4;
export const PHASE_NOYAU = 2.2;
/** Instant où la poursuite cesse : début du télégraphe de la salve. */
export const PHASE_VERROU = 2.8;
export const PHASE_SALVE = 3.6;
/** Début de la fenêtre de punition. */
export const PHASE_PUNITION = 5.2;

/** `firstDelay` d'une arme qui doit tirer à la phase `p` de chaque cycle.
 *
 *  ⚠️ Le résultat doit rester supérieur au télégraphe du porteur ET à 0,6 s,
 *  faute de quoi `emitterSystem` annule la volée tout en réarmant le cooldown :
 *  l'arme perdrait sa PREMIÈRE salve en silence, et le premier cycle du boss
 *  serait discrètement plus faible que tous les suivants. */
export function firstDelayAt(p: number): number {
  return COMBAT_START + p;
}

/** Vitesse de poursuite latérale du porteur, en u/s.
 *
 *  Volontairement inférieure de moitié à celle du joueur (480 u/s) : le joueur
 *  doit pouvoir SEMER le boss après le verrou, sinon la leçon « je le pilote »
 *  se retourne en « il me colle », ce qui est le contraire de l'intention. */
export const TRACK_SPEED = 190;

/** Amplitude latérale maximale du porteur, en unités.
 *
 *  ⚠️ Ce n'est pas un réglage esthétique. Les bras sont à ±200 u du noyau et le
 *  demi-terrain vaut 450 : au-delà de 220, un bras sortirait du cadre et
 *  tirerait depuis le hors-champ. Une partie n'est jamais éliminée par la marge
 *  de culling (motionSystem la repositionne puis passe), donc rien ne
 *  rattraperait l'erreur — le joueur recevrait simplement des projectiles nés
 *  d'un point qu'il ne voit pas. */
export const SWEEP_LIMIT = 220;

/* ── Garde-fou de conception ────────────────────────────────────────────────
   Le module échoue à l'import plutôt que de laisser un boss subtilement injouable
   partir en production. Même esprit que `validate()` du registre : une erreur
   de contenu doit être bruyante au chargement, jamais silencieuse en jeu. */
if (CYCLE - PHASE_PUNITION < 1.8) {
  throw new Error(
    `Héraut : fenêtre de punition de ${(CYCLE - PHASE_PUNITION).toFixed(2)} s, ` +
      `minimum 1,80 s (PLAN.md §6.6).`,
  );
}
if (PHASE_SALVE - PHASE_VERROU < 0.5) {
  throw new Error(
    `Héraut : télégraphe de salve de ${(PHASE_SALVE - PHASE_VERROU).toFixed(2)} s, ` +
      `trop court pour être lu.`,
  );
}
